import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useResendForgotPasswordOTP } from '~entities/users/api/resend-forgot-password-otp';
import { useVerifyForgotPassword } from '~entities/users/api/verify-forgot-password';

import { Button } from '~shared/shadcn/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '~shared/shadcn/ui/input-otp';

const validatePassword = (password: string) => {
  const requirements = {
    uppercase: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    minLength: password.length >= 6,
  };
  return requirements;
};

const forgotPasswordSchema = z
  .object({
    otp: z.string().length(6, 'OTP must be 6 digits'),
    newPassword: z
      .string()
      .min(6, 'Password must be at least 6 characters long')
      .refine(
        (password) => /[A-Z]/.test(password),
        'Password must contain at least one uppercase letter',
      )
      .refine(
        (password) => /\d/.test(password),
        'Password must contain at least one number',
      )
      .refine(
        (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
        'Password must contain at least one special character',
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const Component = () => {
  const { locationId, email } = Route.useSearch();

  const verifyForgotPasswordMutation = useVerifyForgotPassword();
  const resendOTPMutation = useResendForgotPasswordOTP();

  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const watchNewPassword = forgotPasswordForm.watch('newPassword');
  const [requirements, setRequirements] = useState(
    validatePassword(watchNewPassword),
  );
  const [countdown, setCountdown] = useState(60);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setRequirements(validatePassword(watchNewPassword));
  }, [watchNewPassword]);

  const startCountdown = () => {
    // Clear any existing interval
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    // Start countdown when component mounts
    startCountdown();

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Reset countdown when OTP is successfully resent
    if (resendOTPMutation.isSuccess) {
      startCountdown();
    }
  }, [resendOTPMutation.isSuccess]);

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    if (!email || !locationId) {
      return;
    }

    try {
      await verifyForgotPasswordMutation.mutateAsync({
        email: email,
        locationId: locationId,
        otp: Number(values.otp),
        newPassword: values.newPassword,
      });
    } catch (error: unknown) {
      // Error is already handled by the mutation's onError
      console.error('Password reset failed:', error);
    }
  };

  const handleResendOTP = async () => {
    if (!email || !locationId) {
      return;
    }

    try {
      await resendOTPMutation.mutateAsync({
        email: email,
        locationId: locationId,
      });
    } catch (error: unknown) {
      // Error is already handled by the mutation's onError
      console.error('Resend OTP failed:', error);
    }
  };

  return (
    <div className='flex h-screen w-screen items-center justify-center bg-gray-50'>
      <div className='w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg'>
        <div className='text-center'>
          <div className='mb-4 flex justify-center'>
            <img
              src='/logo.svg'
              alt='Enrollio Logo'
              className='h-12'
              style={{ maxWidth: '180px' }}
            />
          </div>
          <h1 className='text-3xl font-bold text-gray-900'>Reset Password</h1>
          <p className='mt-2 text-sm text-gray-600'>
            Enter the OTP sent to your email and set a new password
          </p>
        </div>

        <Form {...forgotPasswordForm}>
          <form
            onSubmit={forgotPasswordForm.handleSubmit(onSubmit)}
            className='space-y-6'
          >
            <FormField
              control={forgotPasswordForm.control}
              name='otp'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>One-Time Password</FormLabel>
                  <FormControl>
                    <InputOTP maxLength={6} {...field}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={forgotPasswordForm.control}
              name='newPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input
                      type='password'
                      placeholder='Enter new password (min 6 characters)'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='mt-2 space-y-1 text-sm'>
              <p
                className={
                  requirements.minLength ? 'text-green-600' : 'text-gray-500'
                }
              >
                • Minimum 6 characters
              </p>
              <p
                className={
                  requirements.uppercase ? 'text-green-600' : 'text-gray-500'
                }
              >
                • At least one uppercase letter
              </p>
              <p
                className={
                  requirements.digit ? 'text-green-600' : 'text-gray-500'
                }
              >
                • At least one number
              </p>
              <p
                className={
                  requirements.symbol ? 'text-green-600' : 'text-gray-500'
                }
              >
                {`• At least one special character (!@#$%^&*(),.?":{|})<>`}
              </p>
            </div>

            <FormField
              control={forgotPasswordForm.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <Input
                      type='password'
                      placeholder='Confirm new password'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type='submit'
              className='w-full'
              disabled={verifyForgotPasswordMutation.isPending}
            >
              {verifyForgotPasswordMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Resetting password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>

            <div className='text-center'>
              <button
                type='button'
                onClick={handleResendOTP}
                disabled={resendOTPMutation.isPending || countdown > 0}
                className='text-sm text-blue-600 underline hover:text-blue-800 disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline'
              >
                {resendOTPMutation.isPending ? (
                  <>
                    <Loader2 className='mr-1 inline size-3 animate-spin' />
                    Resending...
                  </>
                ) : countdown > 0 ? (
                  `Resend OTP in ${countdown}s`
                ) : (
                  "Didn't receive the code? Resend OTP"
                )}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

const forgotPasswordSearchParamSchema = z.object({
  locationId: z.string(),
  email: z.string(),
});

export const Route = createFileRoute('/forgot-password')({
  validateSearch: zodValidator(forgotPasswordSearchParamSchema),
  component: Component,
});
